import { useState, useEffect, useCallback, useMemo } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { database } from '../lib/firebase';
import {
  supabase,
} from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { formatTimeAgo } from '../lib/formatTimeAgo';

// Extended senior type for dashboard display
export interface SeniorLocation {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: number;
}

export interface Senior {
  id: string;
  name: string;
  caregiver_id: string;
  profile_type?: 'cognitive' | 'physical' | 'visual';
  severity?: 'severe' | 'moderate' | 'mild';
  plan_tier?: 'plus';
  layout_id?: string;
  status: 'online' | 'offline' | 'attention';
  battery?: number;
  last_seen?: string;
  device_name?: string;
  activation_code?: string;
  activated?: boolean;
  contact_count?: number;
  location?: SeniorLocation;
  subscription_status?: 'trial' | 'active' | 'cancelled' | 'expired';
  trial_ends_at?: string;
  has_payment?: boolean;
  stripe_customer_id?: string;
  /**
   * Android Call Screening role grant state, written by the AidFone app at
   * `devices/{seniorId}/status/scamBlockingActive`. `undefined` means the
   * device hasn't reported yet (pre-rollout build or never activated) — treat
   * as "unknown" and do not surface a warning. Only `false` triggers the
   * "Scam blocking inactive" banner.
   */
  scamBlockingActive?: boolean;
  /**
   * F05 battery-alert state, straight from `configurations`. Surfaced on the
   * Senior so the dashboard tile can show current state without opening the
   * drawer. `undefined` means the configuration row hasn't loaded yet — render
   * the static subtitle rather than guessing "Off".
   */
  voice_nag_enabled?: boolean;
  lowbat_sms_enabled?: boolean;
  /**
   * D8 — scam-call blocking, for the tile's live ON/OFF state. `undefined`
   * while the configuration row is unread; the tile shows no state dot then
   * rather than claiming the senior is unprotected.
   */
  call_screening_enabled?: boolean;
}

export interface DashboardStats {
  activeSeniors: number;
  pendingUpdates: number;
  systemAlerts: number;
}

interface FirebaseDeviceStatus {
  isOnline: boolean;
  batteryLevel?: number;
  lastSeen?: string;
  deviceManufacturer?: string;
  deviceModel?: string;
  scamBlockingActive?: boolean;
}


export function useSeniors() {
  const { user } = useAuth();
  // baseData — static Supabase data (profile, contacts, config)
  const [baseData, setBaseData] = useState<Senior[]>([]);
  // liveStatus — Firebase RTDB live device status, keyed by senior ID
  const [liveStatus, setLiveStatus] = useState<Record<string, FirebaseDeviceStatus>>({});
  // liveLocation — Firebase RTDB live location, keyed by senior ID
  const [liveLocation, setLiveLocation] = useState<Record<string, SeniorLocation>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Merge: Supabase base data overridden by Firebase live status
  // This never clobbers — each source updates its own slice independently
  const seniors = useMemo<Senior[]>(() =>
    baseData.map(s => {
      const fb = liveStatus[s.id];
      const loc = liveLocation[s.id];
      const base = fb ? (() => {
        const isOnline = fb.isOnline === true;
        const deviceName = [fb.deviceManufacturer, fb.deviceModel].filter(Boolean).join(' ');
        return {
          ...s,
          status: (isOnline ? 'online' : (s.activated ? 'offline' : 'attention')) as Senior['status'],
          battery: typeof fb.batteryLevel === 'number' ? fb.batteryLevel : s.battery,
          last_seen: isOnline ? undefined : (fb.lastSeen ? formatTimeAgo(fb.lastSeen) : s.last_seen),
          device_name: deviceName || s.device_name,
          scamBlockingActive: typeof fb.scamBlockingActive === 'boolean' ? fb.scamBlockingActive : s.scamBlockingActive,
        };
      })() : s;
      return { ...base, location: loc };
    }),
    [baseData, liveStatus, liveLocation]
  );

  // Stats derived from merged seniors
  const stats = useMemo(() => ({
    activeSeniors: seniors.filter(s => s.activated).length,
    pendingUpdates: seniors.filter(s => s.status === 'offline').length,
    systemAlerts: seniors.filter(s => s.status === 'attention').length,
  }), [seniors]);

  const fetchSeniors = useCallback(async () => {
    if (!user) {
      setBaseData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const { data: seniorsData, error: seniorsError } = await supabase
        .from('seniors')
        .select(`
          *,
          assessments (*),
          subscriptions (*),
          configurations (*),
          senior_contacts (count),
          device_status (*)
        `)
        .eq('caregiver_id', user.id)
        .order('created_at', { ascending: false });

      if (seniorsError) throw seniorsError;

      const mapped: Senior[] = (seniorsData || []).map((senior: any) => {
        const assessment = senior.assessments?.[0];
        const subscription = senior.subscriptions?.[0];
        const configuration = senior.configurations?.[0];
        const contactCount = senior.senior_contacts?.[0]?.count || 0;
        const deviceStatus = senior.device_status;
        const isActivated = !!configuration?.activated_at;

        // Supabase-based status — Firebase will override this via liveStatus
        const status: 'online' | 'offline' | 'attention' = deviceStatus?.is_online
          ? 'online'
          : isActivated ? 'offline' : 'attention';

        let lastSeen: string | undefined;
        if (!deviceStatus?.is_online && deviceStatus?.last_seen) {
          lastSeen = formatTimeAgo(deviceStatus.last_seen);
        }

        const deviceName = deviceStatus
          ? [deviceStatus.device_manufacturer, deviceStatus.device_model].filter(Boolean).join(' ')
          : undefined;

        return {
          id: senior.id,
          name: senior.name,
          caregiver_id: senior.caregiver_id,
          profile_type: assessment?.profile_type,
          severity: assessment?.severity,
          plan_tier: subscription?.plan_tier,
          layout_id: configuration?.layout_id,
          status,
          battery: deviceStatus?.battery_level ?? undefined,
          last_seen: lastSeen,
          device_name: deviceName || undefined,
          activation_code: configuration?.activation_code,
          activated: isActivated,
          contact_count: contactCount,
          subscription_status: subscription?.status,
          trial_ends_at: subscription?.trial_ends_at,
          has_payment: !!subscription?.stripe_subscription_id,
          stripe_customer_id: subscription?.stripe_customer_id,
          voice_nag_enabled: configuration?.voice_nag_enabled,
          lowbat_sms_enabled: configuration?.lowbat_sms_enabled,
          call_screening_enabled: configuration?.call_screening_enabled,
        };
      });

      setBaseData(mapped);
    } catch (err) {
      console.error('Error fetching seniors:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch seniors');
      setBaseData([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSeniors();
  }, [fetchSeniors]);

  // Firebase live-status listeners — keyed by senior ID
  // Re-runs only when the set of senior IDs changes, not on every Supabase refetch
  const seniorIdKey = baseData.map(s => s.id).join(',');
  useEffect(() => {
    if (!seniorIdKey) return;

    const ids = seniorIdKey.split(',');
    const statusRefs = ids.map(id => ref(database, `devices/${id}/status`));
    const locationRefs = ids.map(id => ref(database, `devices/${id}/location`));

    statusRefs.forEach((statusRef, i) => {
      const seniorId = ids[i];
      onValue(statusRef, (snapshot) => {
        if (!snapshot.exists()) return;
        const fb = snapshot.val() as FirebaseDeviceStatus;
        setLiveStatus(prev => ({ ...prev, [seniorId]: fb }));
      });
    });

    locationRefs.forEach((locRef, i) => {
      const seniorId = ids[i];
      onValue(locRef, (snapshot) => {
        if (!snapshot.exists()) return;
        const loc = snapshot.val() as SeniorLocation;
        if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
          setLiveLocation(prev => ({ ...prev, [seniorId]: loc }));
        }
      });
    });

    return () => {
      statusRefs.forEach(r => off(r));
      locationRefs.forEach(r => off(r));
    };
  }, [seniorIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    seniors,
    stats,
    loading,
    error,
    refetch: fetchSeniors,
  };
}

export default useSeniors;
